import { Alert, Divider, Skeleton, TextField } from '@mui/material';
import { useState } from 'react';

import { createUser } from '../../api/business/endpoints';
import type { CreateUserInput, Grant } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { FormDialog } from '../../components/FormDialog';
import { PermissionMatrix } from '../../components/PermissionMatrix';
import { useT } from '../../i18n/LanguageContext';

import { useGrantContext } from './useGrantContext';

/**
 * A sub-user with their grants — never more than the person creating them holds (BR-11). The user is invited
 * by mail and chooses their own password (R0005 D-07).
 */
export const CreateUserDialog = ({ onClose }: { onClose: () => void }) => {
  const t = useT();
  const context = useGrantContext();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [grants, setGrants] = useState<Array<Grant>>([]);
  const mutation = useAppMutation(
    (input: CreateUserInput) => createUser(input),
    ['users', 'licence-usage'],
    onClose,
  );

  return (
    <FormDialog
      title={t('users.create')}
      onClose={onClose}
      onSubmit={() =>
        mutation.mutate({
          email: email.trim(),
          displayName: displayName.trim() || undefined,
          grants,
        })
      }
      submitLabel={t('common.create')}
      isBusy={mutation.isPending}
      error={mutation.error ?? context.error}
      maxWidth="md"
    >
      <TextField
        required
        type="email"
        label={t('field.email')}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <TextField
        label={t('field.displayName')}
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
      />
      <Alert severity="info" variant="outlined">
        {t('invitation.hint')}
      </Alert>
      <Divider />
      {context.isLoading ? (
        <Skeleton variant="rounded" height={160} />
      ) : (
        <PermissionMatrix
          definitions={context.definitions}
          companies={context.companies}
          value={grants}
          onChange={setGrants}
          canGrant={context.canGrant}
        />
      )}
    </FormDialog>
  );
};
